/**
 * Pure derivations from a scan result. Kept out of the component so the
 * fallback behaviour is testable in isolation.
 */

/**
 * Modules to offer, from what the scan actually found.
 *
 * The backend refuses to guess which ODD product a managed product maps to
 * (F5 returns all 29 as candidates), so the user must choose. Showing the
 * modules the fleet actually runs turns the scan into something that narrows
 * the choice. When there is no profile — STRY0038262 absent, or a tenant whose
 * CIs expose no module config — fall back to the full list rather than
 * offering nothing.
 */
export function moduleOptions(snapshot, allProducts) {
  const profile = (snapshot && snapshot.componentProfile) || [];
  const detected = profile
    .filter(c => c && c.enabledCiCount > 0)
    .sort((a, b) => b.enabledCiCount - a.enabledCiCount || a.component.localeCompare(b.component))
    .map(c => ({
      value: c.component,
      label: `${c.component} (${c.enabledCiCount} device${c.enabledCiCount === 1 ? '' : 's'})`,
      enabledCiCount: c.enabledCiCount
    }));
  if (detected.length) return detected;
  return allProducts || [];
}

/**
 * Whether the scan actually found any modules to narrow the choice with —
 * i.e. whether `moduleOptions` is showing the detected list rather than
 * having fallen back to the full product list. Used to decide whether the
 * "show all" escape hatch (a module in use but absent from CMDB config) is
 * needed: when there's no detected list, the full list is already showing.
 */
export function hasDetectedModules(snapshot) {
  const profile = (snapshot && snapshot.componentProfile) || [];
  return profile.some(c => c && c.enabledCiCount > 0);
}

/**
 * The fleet's version groups, largest first.
 *
 * A fleet is rarely on one version, and the devices that are *not* on the
 * majority are usually the ones that need attention. Presenting the whole
 * distribution — "15 devices on 16.1.2.2, 10 on 16.1.2.1" — lets a reviewer
 * start a project for any of them rather than only the majority.
 *
 * Each group becomes its own project: a project reviews upgrade candidates
 * from one current version, so two groups on different versions have
 * different candidate sets and merging them would misstate both.
 */
export function versionGroups(snapshot) {
  const s = snapshot || {};
  const versions = Array.isArray(s.versions) ? s.versions : [];
  const groups = versions
    .filter(v => v && v.version && v.ciCount > 0)
    .map(v => ({ version: v.version, ciCount: v.ciCount }))
    .sort((a, b) => b.ciCount - a.ciCount || String(a.version).localeCompare(String(b.version)));
  if (groups.length) return groups;

  // A snapshot with no breakdown — an older one, or a vendor whose adapter
  // does not report per-version counts — still has a majority. Synthesising
  // the one group keeps such a fleet creatable; returning nothing would
  // render an empty list and disable Create, turning missing detail into a
  // dead end.
  if (s.majorityVersion) {
    return [{ version: s.majorityVersion, ciCount: s.matchingCount || s.totalCis || 0 }];
  }
  return [];
}

/**
 * The default project name for one version group.
 *
 * Leads with the device, because that is what the customer owns and
 * recognises. The module deliberately does not appear: a project is a device
 * plus a version, and naming it after one of the device's modules is what
 * made these read as "F5 PEM" rather than "the i4600 fleet".
 */
export function versionGroupProjectName(managedProductName, version) {
  return [managedProductName, version].filter(Boolean).join(' — ');
}

export function fleetSummary(snapshot) {
  const s = snapshot || {};
  const inconsistentCount = s.inconsistentCount || 0;
  return {
    totalCis: s.totalCis || 0,
    matchingCount: s.matchingCount || 0,
    inconsistentCount,
    majorityVersion: s.majorityVersion || null,
    isSplit: inconsistentCount > 0
  };
}

export function defaultProjectName(managedProductName, productId, version) {
  const tail = [productId, version].filter(Boolean).join(' ');
  return [managedProductName, tail].filter(Boolean).join(' — ');
}
